// 1:1 port from age-of-mcp v3 (house.ts). The flattened part literals are
// reconstructed from the original helper functions; geometry is parity-gated
// identical.
//
// v3 house — HOME, read from above. One limewashed cottage body under a big
// terracotta gable (ROOF_HOME) with deep eaves, and the iconic top feature: an
// oversized stone chimney breaking the back roof plane, capped dark. A timber
// sill band, a framed door and a single window face the street (+z).

import { defineModel, styleV3, type Part } from "../../lib";

const { PLASTER, TIMBER, STONE, STONE_DK, DOOR, ROOF_HOME, STEEL, box, slab, poles, gable } = styleV3;

const BASE   = 0.12;          // top of the foundation slab
const W      = 0.72;          // body width (x)
const D      = 0.68;          // body depth (z)
const WALL_H = 0.62;
const FRONT  = D / 2;         // the street face (+z)

// The plaster body, `h` tall on the foundation.
function body(h: number): Part {
  return box(W, h, D, PLASTER, [0, BASE + h / 2, 0]);
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.86, 0.86)];
  if (s === 1) return [slab(0.86, 0.86), body(0.3), ...poles(0.8, 0.76, 0.6)];
  if (s === 2) return [slab(0.86, 0.86), body(WALL_H), ...poles(0.8, 0.76, 1.0)];

  const top = BASE + WALL_H;          // 0.74 — the eave line
  return [
    slab(0.86, 0.86),
    body(WALL_H),
    // Oak sill band ringing the body at knee height.
    box(W + 0.03, 0.05, D + 0.03, TIMBER, [0, BASE + 0.16, 0]),
    // The big terracotta gable — ridge along x, eaves overhanging. ONE part.
    gable(0.8, 0.84, 0.42, ROOF_HOME, 0, top, 0, "x"),
    // Oversized stone chimney breaking the back roof plane, dark cap.
    box(0.16, 0.6, 0.16, STONE,    [0.2, top + 0.21, -0.16]),
    box(0.21, 0.05, 0.21, STONE_DK, [0.2, top + 0.535, -0.16]),
    // Street face: a framed door and a shuttered-dark window.
    box(0.2, 0.4, 0.04, DOOR,     [-0.14, BASE + 0.2, FRONT + 0.01]),
    box(0.27, 0.05, 0.05, TIMBER, [-0.14, BASE + 0.43, FRONT + 0.01]),
    box(0.16, 0.16, 0.04, STEEL,  [0.2, BASE + 0.38, FRONT + 0.01]),
  ];
}

export const houseV3 = defineModel({
  id: "house-v3",
  tiles: { w: 1, h: 1 },
  batchId: "45-optimized",
  name: "House (v3)",
  tags: ["building","house","low-poly","age-of-mcp","v3"],
  summary: "Low-poly house.",
  description: "A small wooden dwelling.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
